import { useCallback, useEffect, useMemo } from 'react';
import { LogicType, ActionType, useAttachedCartItemReferenceLogics } from '@kidslabs/module-quote';
import { type Observable } from '@legendapp/state';
import { useProductGiftQuery, type FreeGiftsData, ProductStockStatus } from '../../../types/front.graphql';
import { type SelectedGift } from '../types/gift-selection.types';
import { productGiftsStore$ } from '../stores/product-gifts.store';
import { isActiveDate } from '@kidslabs/module-core';

/**
 * Minimal product type required for gift hooks
 * This allows accepting both full ProductInterface and partial product-like objects
 */
interface ProductLike {
    sku?: string | null;
    id?: number | string | null;
    name?: string | null;
}

interface ProductGiftHookProps {
    product?: ProductLike;
    fetchGiftsFromCache?: boolean; // Optional prop to fetch from cache
    observeStore?: boolean; // Optional prop to observe store instead of query
}

interface AddGiftToCartItemReferenceLogicProps {
    selectedGifts$: Observable<SelectedGift[]> | undefined;
    sessionId: string;
}

interface ProductGiftHook {
    loading: boolean;
    error: Error | undefined;
    sortedGiftsDataList: FreeGiftsData[];
    giftsDataList: FreeGiftsData[];
    addGiftToCartItemReferenceLogic: ({ selectedGifts$, sessionId }: AddGiftToCartItemReferenceLogicProps) => void;
}

export function useProductGiftHook({
    product,
    fetchGiftsFromCache = false,
    observeStore = false,
}: ProductGiftHookProps): ProductGiftHook {
    const { addAttachedCartItemReferenceLogic, getCartItemReferenceId } = useAttachedCartItemReferenceLogics();
    const sku = product?.sku ?? 'no-sku-gift';

    // Query gifts data (skip if observeStore is true and data exists in store)
    const shouldSkipQuery = observeStore;
    // && productGiftsStore$[sku].peek() !== undefined;

    const { data, loading, error } = useProductGiftQuery({
        variables: {
            sku,
        },
        fetchPolicy: fetchGiftsFromCache ? 'cache-only' : 'cache-first',
        skip: shouldSkipQuery,
    });

    // Process gifts data with useMemo to prevent unnecessary re-renders
    // Filter out out-of-stock products as a safety net (backend also filters, but cache may be stale)
    const giftItems = useMemo(() => {
        const filteredItems = data?.freeGifts?.items
            ?.filter(giftItem => giftItem !== null && giftItem?.rule?.simple_action === 'ampromo_eachn')
            ?.filter(giftItem => {
                if (!giftItem?.rule?.end_date) {
                    return false;
                }
                return isActiveDate(giftItem?.rule?.from_date ?? undefined, giftItem?.rule?.end_date);
            })
            ?.map(giftItem => ({
                ...giftItem,
                products: giftItem?.products?.filter(
                    product =>
                        product?.__typename === 'SimpleProduct' && product?.stock_status === ProductStockStatus.InStock
                ),
            }))
            ?.filter(giftItem => giftItem?.products && giftItem.products.length > 0);

        return (filteredItems ?? []) as FreeGiftsData[];
    }, [data]);

    const sortedGiftItems = useMemo(() => {
        return giftItems.sort((a, b) => (a.rule?.discount_step ?? 0) - (b.rule?.discount_step ?? 0));
    }, [giftItems]);

    // Save to store when data is loaded
    useEffect(() => {
        if (data && !loading && !error) {
            productGiftsStore$[sku].set({
                giftsDataList: giftItems,
                sortedGiftsDataList: sortedGiftItems,
                loading: false,
                error: undefined,
                updatedAt: new Date().toISOString(),
            });
        }
    }, [data, loading, error, sku, giftItems, sortedGiftItems]);

    // Update loading state in store
    useEffect(() => {
        if (loading) {
            const currentData = productGiftsStore$[sku].peek();
            productGiftsStore$[sku].set({
                ...(currentData || {
                    giftsDataList: [],
                    sortedGiftsDataList: [],
                }),
                loading: true,
            });
        }
    }, [loading, sku]);

    // Update error state in store
    useEffect(() => {
        if (error) {
            const currentData = productGiftsStore$[sku].peek();
            productGiftsStore$[sku].set({
                ...(currentData || {
                    giftsDataList: [],
                    sortedGiftsDataList: [],
                    loading: false,
                }),
                error: error.message,
            });
        }
    }, [error, sku]);

    // Observe store data when observeStore is true
    const storeData = observeStore ? productGiftsStore$[sku].get() : undefined;

    const addGiftToCartItemReferenceLogic = useCallback(
        ({ selectedGifts$, sessionId }: AddGiftToCartItemReferenceLogicProps) => {
            const selectedGifts: SelectedGift[] = selectedGifts$?.peek() ?? [];

            console.log('[useProductGiftHook] addGiftToCartItemReferenceLogic called:', {
                sessionId,
                selectedGifts,
                selectedGiftsCount: selectedGifts.length,
            });

            if (Array.isArray(selectedGifts) && selectedGifts.length > 0) {
                const cartItemReferenceId = getCartItemReferenceId();
                console.log('[useProductGiftHook] cartItemReferenceId:', cartItemReferenceId);

                selectedGifts.forEach(selectedGift => {
                    console.log('[useProductGiftHook] Processing selected gift:', selectedGift);
                    if (selectedGift?.item && selectedGift?.giftData) {
                        const logicToAdd = {
                            cartItemReferenceId: `${cartItemReferenceId}`,
                            logicType: LogicType.PROMOTION,
                            logicId: selectedGift.giftData?.rule?.rule_id ?? 0,
                            logicName: selectedGift.giftData?.rule?.name ?? 'N/A',
                            // allocatedQty = number of purchased items allocated to this rule
                            // for gift calculation (e.g., buy 14 items, rule 1 gets 8, rule 2 gets 6)
                            allocatedQty: selectedGift.quantityUsedByRule,
                            actions: [
                                {
                                    actionType: ActionType.GIFT,
                                    // Cast to any to allow partial product data
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    actionProduct: selectedGift.item as any,
                                    actionValue: selectedGift.quantity,
                                    actionNote: selectedGift.giftData?.rule?.name ?? 'N/A',
                                    actionQty: selectedGift.quantity,
                                    createdAt: new Date().toISOString(),
                                },
                            ],
                            createdAt: new Date().toISOString(),
                            sessionId,
                        };
                        addAttachedCartItemReferenceLogic(logicToAdd);
                    }
                });
            }
        },
        [addAttachedCartItemReferenceLogic, getCartItemReferenceId]
    );

    // Return data from store if observing, otherwise from query
    if (observeStore && storeData) {
        return {
            loading: storeData.loading,
            error: storeData.error ? new Error(storeData.error) : undefined,
            sortedGiftsDataList: storeData.sortedGiftsDataList,
            giftsDataList: storeData.giftsDataList,
            addGiftToCartItemReferenceLogic,
        };
    }

    return {
        loading,
        error,
        sortedGiftsDataList: sortedGiftItems,
        giftsDataList: giftItems,
        addGiftToCartItemReferenceLogic,
    };
}
